import { Controller } from "@hotwired/stimulus"
import PhotoSwipeLightbox from "photoswipe/lightbox"

export default class extends Controller {
  connect() {
    this.lightbox = new PhotoSwipeLightbox({
      gallery: this.element,
      children: "[data-pswp-type='interfaces-video']",
      pswpModule: () => import("photoswipe")
    })

    this.lightbox.addFilter("itemData", (itemData) => {
      const { element } = itemData

      if (element?.dataset.pswpType === "interfaces-video") {
        itemData.videoLabel = element.dataset.interfacesVideoLabel
        itemData.videoPoster = element.dataset.interfacesVideoPoster
        itemData.videoSources = JSON.parse(element.dataset.interfacesVideoSources)
      }

      return itemData
    })

    this.lightbox.on("contentLoad", (event) => {
      const { content } = event

      if (content.type !== "interfaces-video") return

      event.preventDefault()
      content.element = this.buildVideo(content.data)
    })

    this.lightbox.on("contentActivate", ({ content }) => {
      this.videoFor(content)?.play().catch(() => {})
    })

    this.lightbox.on("contentDeactivate", ({ content }) => {
      this.videoFor(content)?.pause()
    })

    this.lightbox.on("contentDestroy", ({ content }) => {
      const video = this.videoFor(content)

      if (!video) return

      video.pause()
      video.removeAttribute("src")
      video.querySelectorAll("source").forEach((source) => source.remove())
      video.load()
    })

    this.lightbox.init()
    this.observePreviews()
  }

  disconnect() {
    this.previewObserver?.disconnect()
    this.previewVideos?.forEach((video) => video.pause())
    this.lightbox.destroy()
    this.previewObserver = null
    this.previewVideos = null
    this.lightbox = null
  }

  observePreviews() {
    this.previewVideos = Array.from(this.element.querySelectorAll(".interfaces-device-video"))

    if (!("IntersectionObserver" in window)) {
      this.previewVideos
        .filter((video) => video.getClientRects().length)
        .forEach((video) => this.playPreview(video))
      return
    }

    this.previewObserver = new IntersectionObserver((entries) => {
      entries.forEach(({ isIntersecting, target }) => {
        if (isIntersecting) {
          this.playPreview(target)
        } else {
          target.pause()
        }
      })
    }, { threshold: 0.25 })

    this.previewVideos.forEach((video) => this.previewObserver.observe(video))
  }

  playPreview(video) {
    this.loadPreview(video)
    video.play().catch(() => {})
  }

  loadPreview(video) {
    if (video.dataset.sourcesLoaded === "true") return

    video.querySelectorAll("source[data-src]").forEach((source) => {
      source.src = source.dataset.src
    })

    if (video.dataset.poster) video.poster = video.dataset.poster

    video.dataset.sourcesLoaded = "true"
    video.load()
  }

  buildVideo(data) {
    const container = document.createElement("div")
    const video = document.createElement("video")

    container.className = "interfaces-lightbox-video-container"
    video.className = "interfaces-lightbox-video"
    video.setAttribute("aria-label", data.videoLabel)
    video.controls = true
    video.loop = true
    video.muted = true
    video.playsInline = true
    video.preload = "metadata"

    if (data.videoPoster) video.poster = data.videoPoster

    data.videoSources.forEach(({ src, type }) => {
      const source = document.createElement("source")

      source.src = src
      source.type = type
      video.appendChild(source)
    })

    container.appendChild(video)
    return container
  }

  videoFor(content) {
    return content.element?.querySelector("video")
  }
}
